import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { BookmarkNode, BrowserSettings, BrowserTab } from '../types';
import { effectiveRenderMode, isInternalUrl, urlOrigin } from '../lib/urls';
import { SEARCH_ENGINES } from '../constants';

interface Props {
  tab?: BrowserTab;
  settings: BrowserSettings;
  bookmarks: BookmarkNode;
  online: boolean;
  onNavigate: (input: string) => void;
  onBack: () => void;
  onForward: () => void;
  onReload: () => void;
  onHome: () => void;
  onToggleBookmark: () => void;
  onOpenSiteInfo: () => void;
  onSaveOffline: () => void;
  onReadingMode: () => void;
  onDevTools: () => void;
  onTranslate: (target: string) => void;
  onOpenInternal: (url: string) => void;
  onCycleRenderMode: () => void;
  onToggleFullscreen: () => void;
  isFullscreen: boolean;
}

function containsBookmark(root: BookmarkNode, url?: string): boolean {
  if (!url) return false;
  if (root.type === 'bookmark' && root.url === url) return true;
  return (root.children || []).some((child) => containsBookmark(child, url));
}

function modeLabel(tab: BrowserTab | undefined, settings: BrowserSettings): string {
  if (!tab || isInternalUrl(tab.url)) return 'Mode';
  const chosen = tab.renderMode || settings.defaultRenderMode || 'smart';
  const actual = effectiveRenderMode(tab.url, tab.renderMode, settings.defaultRenderMode || 'smart');
  if (chosen === 'smart') {
    if (actual === 'native') return 'Smart:Native';
    return `Smart:${actual === 'direct' ? 'Direct' : 'Proxy'}`;
  }
  if (chosen === 'native') return 'Native';
  return chosen === 'direct' ? 'Direct' : 'Proxy';
}

export function Toolbar({ tab, settings, bookmarks, online, onNavigate, onBack, onForward, onReload, onHome, onToggleBookmark, onOpenSiteInfo, onSaveOffline, onReadingMode, onDevTools, onTranslate, onOpenInternal, onCycleRenderMode, onToggleFullscreen, isFullscreen }: Props) {
  const [value, setValue] = useState(tab?.displayUrl || '');
  useEffect(() => setValue(tab?.displayUrl || ''), [tab?.id, tab?.displayUrl]);
  const bookmarked = useMemo(() => containsBookmark(bookmarks, tab?.url), [bookmarks, tab?.url]);
  const secure = tab?.url.startsWith('https://') || isInternalUrl(tab?.url || '');
  const engineLabel = SEARCH_ENGINES[settings.searchProvider]?.label || 'Search';

  function submit(event: FormEvent) {
    event.preventDefault();
    onNavigate(value);
  }

  return (
    <div className="toolbar">
      <div className="nav-buttons">
        <button onClick={onBack} disabled={!tab?.canGoBack} title="Back">‹</button>
        <button onClick={onForward} disabled={!tab?.canGoForward} title="Forward">›</button>
        <button onClick={onReload} title="Reload">↻</button>
        <button onClick={onHome} title="Home">⌂</button>
      </div>
      <form className="omnibox" onSubmit={submit}>
        <button type="button" className="site-chip" onClick={onOpenSiteInfo} title={urlOrigin(tab?.url || '')}>
          {secure ? '🔒' : '⚠'}
        </button>
        <input value={value} onChange={(event) => setValue(event.target.value)} placeholder={`Search ${engineLabel} or type a URL`} spellCheck={false} />
        <button type="button" className={bookmarked ? 'star active' : 'star'} onClick={onToggleBookmark} title="Bookmark this page">★</button>
      </form>
      <div className="toolbar-actions">
        <span className={`net-dot ${online ? 'online' : 'offline'}`} title={online ? 'Online' : 'Offline'} />
        <button className="mode-button" onClick={onCycleRenderMode} disabled={!tab || isInternalUrl(tab.url)} title="Switch rendering mode: Smart → Native → Direct → Proxy">{modeLabel(tab, settings)}</button>
        <button onClick={onSaveOffline} disabled={!tab || isInternalUrl(tab.url)} title="Make Page Available Offline">⇩ Offline</button>
        <button onClick={onReadingMode} disabled={!tab || isInternalUrl(tab.url)} title="Reading mode">Reader</button>
        <button onClick={onDevTools} disabled={!tab || isInternalUrl(tab.url)} title="Developer mode">Dev</button>
        <select value={settings.translateTarget} onChange={(event) => onTranslate(event.target.value)} title="Translate visible page text">
          <option value="en">EN</option>
          <option value="es">ES</option>
          <option value="fr">FR</option>
          <option value="de">DE</option>
          <option value="ar">AR</option>
        </select>
        <button onClick={onToggleFullscreen} title={isFullscreen ? 'Exit fullscreen (Esc or F11)' : 'Enter fullscreen (F11)'}>{isFullscreen ? '⤢' : '⛶'}</button>
        <button onClick={() => onOpenInternal('websurf://settings')} title="Settings">☰</button>
      </div>
    </div>
  );
}
